import type { FieldSchema, FormSchemaConfig } from "@src/components/form";
import { createDeclarativeResolver } from "./createDeclarativeResolver";
import { getDefaultValueByDeclaration } from "./deriveDefaults";
import type {
    BoundFormConfigForBusinessForm,
    CustomFieldMapSpec,
    DefineFormConfigInput,
    FieldValidationRules,
    FormConfigDefinition,
    FieldMapSpec,
    FormConfigResult,
    Reactive,
    ReasonedBoolean,
    ResolvedFieldValidationRules,
} from "./types";

/**
 * Type-guard для определения object-формы `map` (`{ from?, to? }`).
 * @template TForm тип значений формы
 * @template TDomain тип доменной модели
 * @template TValue тип значения поля
 * @param map спецификация маппинга поля
 * @returns `true`, когда `map` — объектная спецификация
 */
function isMapObject<TForm, TDomain, TValue, TContext = void>(
    map: FieldMapSpec<TForm, TDomain, TValue, TContext>,
): map is CustomFieldMapSpec<TForm, TDomain, TValue, TContext> {
    return map != null && typeof map === "object";
}

/**
 * Нормализует reactive-свойство: возвращает статическое значение или вычисляет функцию от `values`.
 * @template TForm тип значений формы
 * @template TValue тип результирующего значения
 * @param input static/reactive значение
 * @param values текущее состояние формы
 * @param context контекст формы
 * @returns вычисленное значение или `undefined`, если `input` не задан
 */
function getReactiveValue<TForm extends object, TValue, TContext = void>(
    input: Reactive<TForm, TValue, TContext> | undefined,
    values: TForm,
    context: TContext,
): TValue | undefined {
    if (typeof input === "function") {
        return (input as (nextValues: TForm, ctx: TContext) => TValue)(values, context);
    }
    return input;
}

/**
 * Проверяет, что правило `required` включено.
 * Поддерживает формы `true` и `{ value: true }`.
 * @param value описание правила required
 * @returns `true`, если правило `required` активно
 */
function isRequiredRule(value: unknown): boolean {
    if (value === true) return true;
    if (value != null && typeof value === "object" && "value" in value) {
        return (value as { value?: unknown }).value === true;
    }
    return false;
}

/**
 * Сегменты вложенного имени поля массива (`arrayField.${index}.itemField`).
 */
type ParsedNestedFieldPath = {
    /** Имя top-level массива в форме. */
    arrayField: string;
    /** Индекс элемента массива. */
    index: number;
    /** Имя подполя внутри элемента массива. */
    itemField: string;
};

/**
 * Runtime-представление декларации подполя массива для вычисления метаданных и маппинга.
 * @template TContext тип контекста формы
 */
type RuntimeArrayItemDeclaration<TContext = void> = {
    type?: FieldSchema["type"];
    label?: Reactive<Record<string, unknown>, string, TContext>;
    description?: Reactive<Record<string, unknown>, string, TContext>;
    validation?: FieldValidationRules<Record<string, unknown>, TContext, unknown>;
    disabled?: (itemValues: Record<string, unknown>, context: TContext) => ReasonedBoolean;
    visible?: (itemValues: Record<string, unknown>, context: TContext) => ReasonedBoolean;
    map?: FieldMapSpec<Record<string, unknown>, Record<string, unknown>, unknown, TContext>;
};

/**
 * Приводит `ReasonedBoolean` к объектному формату для единообразной обработки.
 * @param value флаг или объект с причиной
 * @returns объект с `value` и опциональным `reason`
 */
function resolveReasonedBoolean(value: ReasonedBoolean | undefined): { value?: boolean; reason?: string } {
    if (value == null) return {};
    if (typeof value === "boolean") return { value };
    return value;
}

/**
 * Разбирает nested path формата `arrayField.${index}.itemField` (V1).
 * @param path имя поля из `react-hook-form`
 * @returns структурированный путь или `null`, если формат не поддержан
 */
function parseNestedFieldPath(path: string): ParsedNestedFieldPath | null {
    const parts = path.split(".");
    if (parts.length !== 3) return null;
    const [arrayField, rawIndex, itemField] = parts;
    const index = Number(rawIndex);
    if (arrayField.length === 0 || itemField.length === 0 || Number.isNaN(index) || index < 0) {
        return null;
    }
    return { arrayField, index, itemField };
}

/**
 * Пытается извлечь статическую часть validation-правил для `schemaConfig`.
 * Если найдено reactive-правило (функция), возвращает `undefined`, чтобы не дублировать runtime-логику.
 * @template TForm тип значений формы
 * @param validation validation-правила декларации поля
 */
function toStaticValidationRules<TForm extends object, TContext = void, TValue = unknown>(
    validation: FieldValidationRules<TForm, TContext, TValue> | undefined,
): FieldSchema["validation"] | undefined {
    if (validation == null) return undefined;
    if (typeof validation.required === "function") return undefined;
    if (typeof validation.minLength === "function") return undefined;
    if (typeof validation.maxLength === "function") return undefined;
    if (typeof validation.min === "function") return undefined;
    if (typeof validation.max === "function") return undefined;
    if (typeof validation.integer === "function") return undefined;
    if (typeof validation.positive === "function") return undefined;
    if (typeof validation.pattern === "function") return undefined;

    const minLength = validation.minLength === false ? undefined : validation.minLength;
    const maxLength = validation.maxLength === false ? undefined : validation.maxLength;
    const min = validation.min === false ? undefined : validation.min;
    const max = validation.max === false ? undefined : validation.max;
    const integer = validation.integer === false ? undefined : validation.integer;
    const positive = validation.positive === false ? undefined : validation.positive;
    const pattern = validation.pattern === false ? undefined : validation.pattern;

    return {
        required: validation.required,
        minLength,
        maxLength,
        min,
        max,
        integer,
        positive,
        pattern,
    };
}

/**
 * Создаёт единый конфиг формы:
 * - `schemaConfig` для `BusinessForm`;
 * - `defaults()` для инициализации;
 * - `toForm(domain)` и `toDomain(values)` для маппинга (контекст для `from`/`to` — только после `withContext`);
 * - `validation.deps` в декларациях — `BusinessForm` собирает из них карту для `trigger` после `setValue`.
 *
 * @template TForm тип значений формы
 * @template TDomain тип доменной модели
 * @param input декларация полей и опциональный `toDomainOverride`
 * @returns готовый набор API для доменной формы
 */
export function defineFormConfig<TForm extends object, TDomain extends object, TContext = void>(
    input: DefineFormConfigInput<TForm, TDomain, TContext>,
): FormConfigDefinition<TForm, TDomain, TContext> {
    const { fields, toDomainOverride } = input;

    const schemaFields = {} as FormSchemaConfig<Record<string, unknown>>["fields"];
    for (const key of Object.keys(fields) as (keyof TForm)[]) {
        const declaration = fields[key];
        const schemaField: FieldSchema = {
            type: declaration.type,
            nullable: declaration.nullable,
            label: typeof declaration.label === "string" ? declaration.label : undefined,
            validation: toStaticValidationRules(declaration.validation),
        };
        schemaFields[key as string] = schemaField;
    }
    const schemaConfig: FormSchemaConfig<Record<string, unknown>> = { fields: schemaFields };
    const resolver = createDeclarativeResolver(fields);

    const defaults = (): TForm => {
        const result = {} as TForm;
        for (const key of Object.keys(fields) as (keyof TForm)[]) {
            result[key] = getDefaultValueByDeclaration(fields[key]);
        }
        return result;
    };

    const runToForm = (domain: TDomain | null | undefined, context: TContext | undefined): TForm => {
        const fallback = defaults();
        if (domain == null) return fallback;

        const result = { ...fallback } as TForm;
        for (const key of Object.keys(fields) as (keyof TForm)[]) {
            const declaration = fields[key];
            const map = declaration.map;

            if (declaration.type === "array" && declaration.itemFields != null) {
                const itemFields = declaration.itemFields as Record<string, RuntimeArrayItemDeclaration<TContext>>;
                let domainArray: unknown[] | null = null;
                if (typeof map === "string") {
                    const mapped = (domain as Record<string, unknown>)[map as string];
                    domainArray = Array.isArray(mapped) ? mapped : null;
                } else if (!isMapObject(map)) {
                    const mapped = (domain as Record<string, unknown>)[key as string];
                    domainArray = Array.isArray(mapped) ? mapped : null;
                }

                if (domainArray != null) {
                    const mappedArray = domainArray.map((domainItem) => {
                        const sourceItem =
                            domainItem != null && typeof domainItem === "object"
                                ? (domainItem as Record<string, unknown>)
                                : ({} as Record<string, unknown>);
                        const targetItem: Record<string, unknown> = { ...sourceItem };
                        for (const itemKey of Object.keys(itemFields)) {
                            const itemDeclaration = itemFields[itemKey];
                            if (itemDeclaration == null) continue;
                            const itemMap = itemDeclaration.map;
                            if (itemMap != null && isMapObject(itemMap) && typeof itemMap.from === "function") {
                                targetItem[itemKey] = itemMap.from(sourceItem, context);
                                continue;
                            }
                            if (typeof itemMap === "string") {
                                targetItem[itemKey] = sourceItem[itemMap];
                                continue;
                            }
                            targetItem[itemKey] = sourceItem[itemKey];
                        }
                        return targetItem;
                    });
                    result[key] = mappedArray as TForm[typeof key];
                    continue;
                }
            }

            if (isMapObject(map) && typeof map.from === "function") {
                const mapped = map.from(domain, context);
                result[key] = (mapped ?? fallback[key]) as TForm[typeof key];
                continue;
            }

            if (typeof map === "string") {
                const mapped = (domain as Record<string, unknown>)[map as string] as TForm[typeof key] | null | undefined;
                result[key] = (mapped ?? fallback[key]) as TForm[typeof key];
                continue;
            }

            // map-объект без `from`: используем fallback значения формы.
            result[key] = fallback[key];
        }
        return result;
    };

    const toForm = (domain: TDomain | null | undefined): TForm => runToForm(domain, undefined);

    const runToDomain = (values: TForm, context: TContext | undefined): Partial<TDomain> => {
        const auto = {} as Partial<TDomain>;
        for (const key of Object.keys(fields) as (keyof TForm)[]) {
            const declaration = fields[key];
            const map = declaration.map;

            if (declaration.type === "array" && declaration.itemFields != null) {
                const itemFields = declaration.itemFields as Record<string, RuntimeArrayItemDeclaration<TContext>>;
                const sourceArray = values[key];
                const formItems = Array.isArray(sourceArray) ? sourceArray : [];
                const mappedItems = formItems.map((item) => {
                    const sourceItem =
                        item != null && typeof item === "object"
                            ? (item as Record<string, unknown>)
                            : ({} as Record<string, unknown>);
                    const targetItem: Record<string, unknown> = { ...sourceItem };
                    for (const itemKey of Object.keys(itemFields)) {
                        const itemDeclaration = itemFields[itemKey];
                        if (itemDeclaration == null) continue;
                        const itemMap = itemDeclaration.map;
                        if (itemMap != null && isMapObject(itemMap) && typeof itemMap.to === "function") {
                            Object.assign(targetItem, itemMap.to(sourceItem[itemKey], sourceItem, context));
                            continue;
                        }
                        if (typeof itemMap === "string") {
                            targetItem[itemMap] = sourceItem[itemKey];
                            continue;
                        }
                        targetItem[itemKey] = sourceItem[itemKey];
                    }
                    return targetItem;
                });

                if (isMapObject(map) && typeof map.to === "function") {
                    Object.assign(auto, map.to(mappedItems as TForm[typeof key], values, context));
                    continue;
                }
                if (typeof map === "string") {
                    (auto as Record<string, unknown>)[map as string] = mappedItems;
                    continue;
                }
            }

            if (isMapObject(map) && typeof map.to === "function") {
                Object.assign(auto, map.to(values[key], values, context));
                continue;
            }

            if (typeof map === "string") {
                (auto as Record<string, unknown>)[map as string] = values[key];
                continue;
            }

            // map-объект без `to`: это осознанный one-way map (`domain -> form`).
        }

        const override = toDomainOverride?.(values, { auto, context }) ?? {};
        return { ...auto, ...override };
    };

    const toDomain = (values: TForm): Partial<TDomain> => runToDomain(values, undefined);

    const resolveFieldMeta: FormConfigResult<TForm, TDomain, TContext>["resolveFieldMeta"] = (name, values, context) => {
        const topLevelDeclaration = (fields as Record<string, (typeof fields)[keyof TForm]>)[name];
        const declaration = topLevelDeclaration ?? null;
        if (declaration == null) {
            const nested = parseNestedFieldPath(name);
            if (nested == null) return {};
            const arrayDeclaration = (fields as Record<string, (typeof fields)[keyof TForm]>)[nested.arrayField] as
                | (typeof fields)[keyof TForm]
                | undefined;
            if (arrayDeclaration == null || arrayDeclaration.type !== "array" || arrayDeclaration.itemFields == null) {
                return {};
            }
            const itemFields = arrayDeclaration.itemFields as Record<string, RuntimeArrayItemDeclaration<TContext>>;
            const itemDeclaration = itemFields[nested.itemField];
            if (itemDeclaration == null) return {};
            const arrayValues = (values as Record<string, unknown>)[nested.arrayField];
            const itemValuesRaw = Array.isArray(arrayValues) ? arrayValues[nested.index] : undefined;
            const itemValues =
                itemValuesRaw != null && typeof itemValuesRaw === "object"
                    ? (itemValuesRaw as Record<string, unknown>)
                    : ({} as Record<string, unknown>);
            const itemValidation = itemDeclaration.validation;
            const resolvedValidation =
                itemValidation == null
                    ? undefined
                    : {
                          required: getReactiveValue(itemValidation.required, itemValues, context),
                          minLength: getReactiveValue(itemValidation.minLength, itemValues, context),
                          maxLength: getReactiveValue(itemValidation.maxLength, itemValues, context),
                          min: getReactiveValue(itemValidation.min, itemValues, context),
                          max: getReactiveValue(itemValidation.max, itemValues, context),
                          integer: getReactiveValue(itemValidation.integer, itemValues, context),
                          positive: getReactiveValue(itemValidation.positive, itemValues, context),
                          pattern: getReactiveValue(itemValidation.pattern, itemValues, context),
                          validate: itemValidation.validate as ResolvedFieldValidationRules<TContext>["validate"],
                      };
            const itemDisabledResolved = resolveReasonedBoolean(itemDeclaration.disabled?.(itemValues, context));
            const itemVisibleResolved = resolveReasonedBoolean(itemDeclaration.visible?.(itemValues, context));
            return {
                label: getReactiveValue(itemDeclaration.label, itemValues, context),
                description: getReactiveValue(itemDeclaration.description, itemValues, context),
                required: isRequiredRule(resolvedValidation?.required),
                disabled: itemDisabledResolved.value,
                disabledReason: itemDisabledResolved.reason,
                visible: itemVisibleResolved.value,
                visibleReason: itemVisibleResolved.reason,
            };
        }

        const resolvedValidation: ResolvedFieldValidationRules<TContext> | undefined =
            declaration.validation == null
                ? undefined
                : {
                      required: getReactiveValue(declaration.validation.required, values, context),
                      minLength: getReactiveValue(declaration.validation.minLength, values, context),
                      maxLength: getReactiveValue(declaration.validation.maxLength, values, context),
                      min: getReactiveValue(declaration.validation.min, values, context),
                      max: getReactiveValue(declaration.validation.max, values, context),
                      integer: getReactiveValue(declaration.validation.integer, values, context),
                      positive: getReactiveValue(declaration.validation.positive, values, context),
                      pattern: getReactiveValue(declaration.validation.pattern, values, context),
                      validate: declaration.validation.validate as ResolvedFieldValidationRules<TContext>["validate"],
                  };

        const disabledResolved = resolveReasonedBoolean(declaration.disabled?.(values, context));
        const visibleResolved = resolveReasonedBoolean(declaration.visible?.(values, context));

        return {
            label: getReactiveValue(declaration.label, values, context),
            description: getReactiveValue(declaration.description, values, context),
            required: isRequiredRule(resolvedValidation?.required),
            disabled: disabledResolved.value,
            disabledReason: disabledResolved.reason,
            visible: visibleResolved.value,
            visibleReason: visibleResolved.reason,
        };
    };

    const bindContext = (context: TContext): BoundFormConfigForBusinessForm<TForm, TDomain> => {
        const boundResolver: BoundFormConfigForBusinessForm<TForm, TDomain>["resolver"] = (values, _context, options) =>
            Promise.resolve(
                (
                    resolver as (
                        values: Record<string, unknown>,
                        context: TContext,
                        options: unknown,
                    ) => Promise<unknown> | unknown
                )(values, context, options),
            ) as ReturnType<BoundFormConfigForBusinessForm<TForm, TDomain>["resolver"]>;

        return {
            schemaConfig,
            resolver: boundResolver,
            resolveFieldMeta: (name, values) => resolveFieldMeta(name, values, context),
            fieldDeclarations: fields,
            defaults,
            toForm: (domain) => runToForm(domain, context),
            toDomain: (values) => runToDomain(values, context),
        };
    };

    const configObject: FormConfigResult<TForm, TDomain, TContext> = {
        schemaConfig,
        resolver,
        fieldDeclarations: fields,
        resolveFieldMeta,
        defaults,
        toForm,
        toDomain,
    };

    return {
        ...configObject,
        withContext: bindContext,
    };
}
